/* ============================================================================
   ADVANCED HERO SLIDER — VANILLA JS CONTROLLER
   No dependencies. No jQuery. Works with multiple instances per page and
   re-initializes correctly inside the Shopify theme editor.
============================================================================ */

(function () {
  'use strict';

  function AdvancedHeroSlider(root) {
    if (!root || root.dataset.ahsInitialized === 'true') return;
    root.dataset.ahsInitialized = 'true';

    this.root = root;
    this.track = root.querySelector('.ahs-track');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('.ahs-slide'));
    this.count = this.slides.length;
    if (this.count === 0) return;

    this.section = root.closest('.ahs');
    this.sectionId = root.dataset.sectionId;

    // ---- Config (read from data attributes set by Liquid) ----
    this.isFade = root.dataset.transition === 'fade';
    this.autoplay = root.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(root.dataset.autoplaySpeed, 10) || 5000;
    this.infinite = root.dataset.infinite === 'true';
    this.transitionSpeed = parseInt(root.dataset.transitionSpeed, 10) || 600;
    this.swipeEnabled = root.dataset.swipe === 'true';
    this.dragEnabled = root.dataset.drag === 'true';
    this.keyboardEnabled = root.dataset.keyboard === 'true';
    this.pauseOnHover = root.dataset.pauseHover === 'true';
    this.heightModeDesktop = root.dataset.heightModeDesktop || 'fixed';
    this.heightModeMobile = root.dataset.heightModeMobile || 'fixed';
    this.mobileBreakpoint = 750;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reducedMotion) this.autoplay = false;

    // ---- DOM refs ----
    this.prevBtn = document.getElementById('AHS-Prev-' + this.sectionId);
    this.nextBtn = document.getElementById('AHS-Next-' + this.sectionId);
    this.dotsWrap = document.getElementById('AHS-Dots-' + this.sectionId);
    this.dots = this.dotsWrap ? Array.prototype.slice.call(this.dotsWrap.querySelectorAll('.ahs-dot')) : [];
    this.fractionCurrent = root.parentElement.querySelector('.ahs-fraction__current');
    this.progressBar = document.getElementById('AHS-Progress-' + this.sectionId);

    // ---- State ----
    this.index = 0;
    this.autoplayTimer = null;
    this.progressStart = null;
    this.progressRAF = null;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragDeltaX = 0;
    this.pointerType = null;

    if (this.isFade) {
      this.slides[0].classList.add('is-active');
    } else {
      this.track.style.transition = 'transform ' + this.transitionSpeed + 'ms ease';
    }

    this.bindEvents();
    this.updateArrowState();
    this.startAutoplay();
    this.applyAutoHeight();
  }

  /* ------------------------------ Auto height ------------------------------ */
  /* Keeps the slider's height matched to the ACTIVE slide's real image aspect
     ratio (per breakpoint) so the picture is shown in full — never cropped
     by object-fit:cover and never visually zoomed/stretched. Falls back to
     the fixed CSS height (set via --ahs-height-desktop/mobile) until this
     runs, so there is still a sane, CLS-safe initial height. */

  AdvancedHeroSlider.prototype.isMobileViewport = function () {
    return window.innerWidth < this.mobileBreakpoint;
  };

  AdvancedHeroSlider.prototype.currentHeightMode = function () {
    return this.isMobileViewport() ? this.heightModeMobile : this.heightModeDesktop;
  };

  AdvancedHeroSlider.prototype.applyAutoHeight = function () {
    var mode = this.currentHeightMode();

    if (mode !== 'auto') {
      this.root.classList.remove('is-auto-height');
      this.root.style.height = '';
      return;
    }

    var activeSlide = this.slides[this.index];
    if (!activeSlide) return;

    var aspectAttr = this.isMobileViewport() ? 'aspectMobile' : 'aspectDesktop';
    var aspectRatio = parseFloat(activeSlide.dataset[aspectAttr]);
    if (!aspectRatio || aspectRatio <= 0) aspectRatio = 16 / 9;

    var width = this.root.clientWidth || this.root.offsetWidth;
    if (!width) return;

    var height = Math.round(width / aspectRatio);
    this.root.classList.add('is-auto-height');
    this.root.style.height = height + 'px';
  };

  AdvancedHeroSlider.prototype.handleResize = function () {
    this.applyAutoHeight();
  };

  AdvancedHeroSlider.prototype.bindEvents = function () {
    var self = this;

    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.userInteracted(); self.prev(); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.userInteracted(); self.next(); });

    this.dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        self.userInteracted();
        self.goTo(i);
      });
    });

    if (this.keyboardEnabled) {
      this.root.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { self.userInteracted(); self.prev(); e.preventDefault(); }
        if (e.key === 'ArrowRight') { self.userInteracted(); self.next(); e.preventDefault(); }
      });
    }

    if (this.pauseOnHover) {
      this.root.addEventListener('mouseenter', function () { self.stopAutoplay(); });
      this.root.addEventListener('mouseleave', function () { self.startAutoplay(); });
      this.root.addEventListener('focusin', function () { self.stopAutoplay(); });
      this.root.addEventListener('focusout', function () { self.startAutoplay(); });
    }

    if (this.swipeEnabled) {
      this.root.addEventListener('touchstart', function (e) { self.onDragStart(e.touches[0].clientX, 'touch'); }, { passive: true });
      this.root.addEventListener('touchmove', function (e) { self.onDragMove(e.touches[0].clientX); }, { passive: true });
      this.root.addEventListener('touchend', function () { self.onDragEnd(); });
    }

    if (this.dragEnabled) {
      this.root.addEventListener('mousedown', function (e) {
        e.preventDefault();
        self.onDragStart(e.clientX, 'mouse');
      });
      window.addEventListener('mousemove', function (e) {
        if (self.isDragging && self.pointerType === 'mouse') self.onDragMove(e.clientX);
      });
      window.addEventListener('mouseup', function () {
        if (self.pointerType === 'mouse') self.onDragEnd();
      });
    }

    // Pause autoplay when slider scrolls out of view (Core Web Vitals friendliness)
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            self.startAutoplay();
          } else {
            self.stopAutoplay();
          }
        });
      }, { threshold: 0.15 });
      this.observer.observe(this.root);
    }

    window.addEventListener('resize', function () {
      if (!self.isFade) self.setTrackPosition(false);
      clearTimeout(self._resizeTimer);
      self._resizeTimer = setTimeout(function () { self.handleResize(); }, 100);
    });
  };

  AdvancedHeroSlider.prototype.userInteracted = function () {
    // Restart the autoplay countdown whenever a person manually navigates.
    this.stopAutoplay();
    this.startAutoplay();
  };

  /* ------------------------------- Drag / swipe ------------------------------ */

  AdvancedHeroSlider.prototype.onDragStart = function (clientX, type) {
    this.isDragging = true;
    this.pointerType = type;
    this.dragStartX = clientX;
    this.dragDeltaX = 0;
    this.root.classList.add('is-dragging');
    this.stopAutoplay();
  };

  AdvancedHeroSlider.prototype.onDragMove = function (clientX) {
    if (!this.isDragging || this.isFade) return;
    this.dragDeltaX = clientX - this.dragStartX;
    var percent = (this.dragDeltaX / this.root.offsetWidth) * 100;
    this.track.style.transform = 'translateX(calc(-' + (this.index * 100) + '% + ' + percent + '%))';
  };

  AdvancedHeroSlider.prototype.onDragEnd = function () {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.root.classList.remove('is-dragging');

    var threshold = this.root.offsetWidth * 0.15;
    if (!this.isFade) {
      if (this.dragDeltaX > threshold) {
        this.prev();
      } else if (this.dragDeltaX < -threshold) {
        this.next();
      } else {
        this.setTrackPosition(true);
      }
    }
    this.dragDeltaX = 0;
    this.startAutoplay();
  };

  /* --------------------------------- Navigation -------------------------------- */

  AdvancedHeroSlider.prototype.next = function () {
    var nextIndex = this.index + 1;
    if (nextIndex >= this.count) {
      if (!this.infinite) return;
      nextIndex = 0;
    }
    this.goTo(nextIndex);
  };

  AdvancedHeroSlider.prototype.prev = function () {
    var prevIndex = this.index - 1;
    if (prevIndex < 0) {
      if (!this.infinite) return;
      prevIndex = this.count - 1;
    }
    this.goTo(prevIndex);
  };

  AdvancedHeroSlider.prototype.goTo = function (i) {
    if (i === this.index || i < 0 || i >= this.count) return;
    var prevSlide = this.slides[this.index];
    this.index = i;

    if (this.isFade) {
      this.slides.forEach(function (s) { s.classList.remove('is-active'); s.setAttribute('aria-hidden', 'true'); });
      this.slides[i].classList.add('is-active');
      this.slides[i].setAttribute('aria-hidden', 'false');
    } else {
      this.setTrackPosition(true);
      this.slides.forEach(function (s, idx) { s.setAttribute('aria-hidden', idx === i ? 'false' : 'true'); });
    }

    this.updateDots();
    this.updateFraction();
    this.updateArrowState();
    this.restartProgress();
    this.applyAutoHeight();
  };

  AdvancedHeroSlider.prototype.setTrackPosition = function (animate) {
    if (this.isFade) return;
    this.track.style.transition = animate ? 'transform ' + this.transitionSpeed + 'ms ease' : 'none';
    this.track.style.transform = 'translateX(-' + (this.index * 100) + '%)';
  };

  AdvancedHeroSlider.prototype.updateDots = function () {
    this.dots.forEach(function (dot, idx) {
      var active = idx === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    }, this);
  };

  AdvancedHeroSlider.prototype.updateFraction = function () {
    if (this.fractionCurrent) this.fractionCurrent.textContent = this.index + 1;
  };

  AdvancedHeroSlider.prototype.updateArrowState = function () {
    if (this.infinite) return;
    if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
    if (this.nextBtn) this.nextBtn.disabled = this.index === this.count - 1;
  };

  /* --------------------------------- Autoplay ----------------------------------- */

  AdvancedHeroSlider.prototype.startAutoplay = function () {
    if (!this.autoplay || this.count < 2 || this.autoplayTimer) return;
    var self = this;
    this.autoplayTimer = setInterval(function () { self.next(); }, this.autoplaySpeed);
    this.runProgress();
  };

  AdvancedHeroSlider.prototype.stopAutoplay = function () {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    if (this.progressBar) {
      this.progressBar.classList.remove('is-animating');
    }
  };

  AdvancedHeroSlider.prototype.restartProgress = function () {
    if (!this.autoplayTimer) return;
    this.stopAutoplay();
    this.startAutoplay();
  };

  AdvancedHeroSlider.prototype.runProgress = function () {
    if (!this.progressBar) return;
    this.progressBar.classList.remove('is-animating');
    this.progressBar.style.width = '0%';
    // Force reflow so the transition re-triggers reliably.
    void this.progressBar.offsetWidth;
    this.progressBar.style.transitionDuration = this.autoplaySpeed + 'ms';
    this.progressBar.classList.add('is-animating');
    this.progressBar.style.width = '100%';
  };

  window.AdvancedHeroSlider = AdvancedHeroSlider;

  // Auto-init any sliders already on the page (covers cases where the inline
  // per-section script did not fire in time, e.g. dynamic section loading).
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.ahs-slider').forEach(function (el) {
      new AdvancedHeroSlider(el);
    });
  });
})();